import React, { useState, useEffect } from 'react';
import { Box, Typography, Button, FormControlLabel, Checkbox } from '@mui/material';

const SvgTab = ({ project, getFullImageUrl, svgFile, setSvgFile, uploadProjectImage }) => {
  const [svgUnits, setSvgUnits] = useState([]);
  const [importElements, setImportElements] = useState(true);

  const parseSvgFile = async (file) => {
    const text = await file.text();
    const parser = new DOMParser();
    const doc = parser.parseFromString(text, 'image/svg+xml');
    const units = buildTreeStructure(doc);
    setSvgUnits(units);
  };

  const parseSvgFromUrl = async (url) => {
    try {
      const response = await fetch(url);
      const text = await response.text();
      const parser = new DOMParser();
      const doc = parser.parseFromString(text, 'image/svg+xml');
      const units = buildTreeStructure(doc);
      setSvgUnits(units);
    } catch (error) {
      console.error('Error parsing SVG from URL:', error);
    }
  };

  const buildTreeStructure = (doc) => {
    const unitGroups = doc.querySelectorAll('g[id^="UNIT"]');
    const tree = [];
    
    unitGroups.forEach(unitGroup => {
      const unitName = unitGroup.id;
      const subunitGroups = unitGroup.querySelectorAll('g[id^="SUBUNIT"]');
      
      if (subunitGroups.length > 0) {
        const nodes = Array.from(subunitGroups).map(sg => ({ name: sg.id }));
        tree.push({ name: unitName, nodes });
      } else {
        tree.push({ name: unitName });
      }
    });
    
    return tree;
  };

  const renderTreeStructure = (units) => {
    return units.map((unit, idx) => (
      <Box key={idx}>
        <Typography sx={{ py: 0.5, fontSize: '0.875rem', fontWeight: 600 }}>
          • {unit.name}
        </Typography>
        {unit.nodes && unit.nodes.map((node, nIdx) => (
          <Typography key={nIdx} sx={{ py: 0.5, fontSize: '0.875rem', pl: 3 }}>
            ◦ {node.name}
          </Typography>
        ))}
      </Box>
    ));
  };

  useEffect(() => {
    if (project?.projectImage?.url?.toLowerCase().includes('.svg') && !svgFile) {
      parseSvgFromUrl(getFullImageUrl());
    }
  }, [project?.projectImage?.url]);

  const handleFileChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      setSvgFile(file);
      parseSvgFile(file);
    }
  };

  return (
    <Box>
      <style>
        {`
          .svg-diagram svg { width: 100%; height: auto; }
          .svg-diagram svg g.group-area { cursor: pointer; }
          .svg-diagram svg g.group-area:hover { opacity: 0.7; }
          .svg-diagram svg g.group-area.highlight path,
          .svg-diagram svg g.group-area.highlight polyline,
          .svg-diagram svg g.group-area.highlight polygon,
          .svg-diagram svg g.group-area.highlight rect,
          .svg-diagram svg g.group-area.highlight circle { 
            stroke: #4CAF50 !important; 
            stroke-width: 5 !important; 
            fill: rgba(76, 175, 80, 0.3) !important; 
          }
        `}
      </style>
      <input
        type="file"
        accept=".svg"
        style={{ display: 'none' }}
        id="svg-upload"
        onChange={handleFileChange}
      />
      <Box sx={{ display: 'flex', gap: 2 }}>
        <Box
          onClick={() => document.getElementById('svg-upload').click()}
          className="svg-diagram"
          sx={{
            flex: 1,
            height: 400,
            border: (svgFile || project?.projectImage?.url?.toLowerCase().includes('.svg')) ? '1px solid #b0d4f1' : '2px dashed #ccc',
            borderRadius: 2,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            overflow: 'hidden',
            p: 2
          }}
        >
          {svgFile ? (
            <img 
              src={URL.createObjectURL(svgFile)} 
              alt="SVG preview" 
              style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
            />
          ) : project?.projectImage?.url?.toLowerCase().includes('.svg') ? (
            <img 
              src={getFullImageUrl()} 
              alt="SVG preview" 
              style={{ maxWidth: '100%', maxHeight: '100%', objectFit: 'contain' }}
            />
          ) : (
            <Typography variant="body2" color="text.secondary">
              Double click to upload SVG file
            </Typography>
          )}
        </Box>
        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 1, height: 400 }}>
          <Box sx={{ flex: 1, border: '1px solid #ddd', borderRadius: 2, overflow: 'auto', p: 2, backgroundColor: '#f9f9f9' }}>
            {svgUnits.length > 0 ? (
              <>
                <Typography variant="subtitle1" sx={{ mb: 2, fontWeight: 600 }}>Found {svgUnits.length} elements:</Typography>
                <Box>
                  {renderTreeStructure(svgUnits)}
                </Box>
              </>
            ) : (
              <Typography variant="body2" color="text.secondary">
                Upload SVG file to see elements
              </Typography>
            )}
          </Box>
          {svgFile && svgUnits.length > 0 && (
            <FormControlLabel
              control={
                <Checkbox 
                  checked={importElements} 
                  onChange={(e) => setImportElements(e.target.checked)}
                  size="small"
                />
              }
              label="Import/update found elements"
            />
          )}
        </Box>
      </Box>
      {(svgFile || project?.projectImage?.url?.toLowerCase().includes('.svg')) && (
        <Box sx={{ mt: 2, display: 'flex', justifyContent: 'space-between', gap: 1 }}>
          <Button
            variant="contained"
            size="small"
            onClick={() => document.getElementById('svg-upload').click()}
          >
            Change file
          </Button>
          {svgFile && (
            <Button
              variant="contained"
              color="success"
              size="small"
              onClick={() => uploadProjectImage(svgFile, importElements ? svgUnits : null)}
            >
              Save
            </Button>
          )}
        </Box>
      )}
      {!svgFile && !project?.projectImage?.url?.toLowerCase().includes('.svg') && (
        <Box sx={{ mt: 2 }}>
          <Button
            variant="contained"
            size="small"
            onClick={() => document.getElementById('svg-upload').click()}
          >
            Add Image
          </Button>
        </Box>
      )}
    </Box>
  );
};

export default SvgTab;
